import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'

const PublicChatWidget = () => {
  const [open, setOpen] = useState(false)
  const [chatId, setChatId] = useState(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [initialMsg, setInitialMsg] = useState('')
  const [messages, setMessages] = useState([])
  const [outgoing, setOutgoing] = useState('')
  const [loading, setLoading] = useState(false)
  const [unread, setUnread] = useState(0)
  const [lastViewedAt, setLastViewedAt] = useState(() => {
    try { return localStorage.getItem('publicChatLastViewedAt') || null } catch { return null }
  })
  const [greeted, setGreeted] = useState(() => {
    try { return localStorage.getItem('publicChatGreeted') === '1' } catch { return false }
  })
  const messagesEndRef = React.useRef(null)
  const fileInputRef = React.useRef(null)
  const [uploadingFile, setUploadingFile] = useState(false)
  const realtimeChannelRef = React.useRef(null)
  const seenMessageIdsRef = React.useRef(new Set())

  useEffect(() => {
    const saved = localStorage.getItem('publicChatId')
    if (saved) setChatId(saved)
  }, [])

  const startChat = async (e) => {
    e.preventDefault()
    if (!name.trim() || !email.trim() || !initialMsg.trim()) return
    setLoading(true)
    try {
      const { data, error } = await supabase.rpc('plc_find_or_start', {
        p_name: name.trim(), p_email: email.trim(), p_phone: phone.trim(), p_message: initialMsg.trim()
      })
      if (error) throw error
      const id = data
      setChatId(id)
      localStorage.setItem('publicChatId', id)
      setOpen(true)
      setInitialMsg('')
      await loadMessages(id)
      if (!greeted) {
        try {
          await supabase.rpc('plc_add_message', { p_chat_id: id, p_sender: 'admin', p_message: 'How can I help you, sir?' })
          setGreeted(true)
          try { localStorage.setItem('publicChatGreeted', '1') } catch {}
          await loadMessages(id)
        } catch {}
      }
    } catch (err) {
      alert(err.message || 'Failed to start chat')
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async (id) => {
    try {
      const { data } = await supabase.rpc('plc_list_messages', { p_chat_id: id })
      setMessages(data || [])
    } catch { setMessages([]) }
  }

  const sendMessage = async (e) => {
    e.preventDefault()
    const msg = outgoing.trim()
    if (!msg || !chatId) return
    try { console.log('[PublicChatWidget] visitor send start', { chatId, textLength: msg.length }) } catch {}
    setOutgoing('')
    setMessages(prev => [...prev, { id: `${Date.now()}`, chat_id: chatId, sender: 'visitor', message: msg, created_at: new Date().toISOString() }])
    try {
      await supabase.rpc('plc_add_message', { p_chat_id: chatId, p_sender: 'visitor', p_message: msg })
      await loadMessages(chatId)
    } catch (err) {
      try { console.error('[PublicChatWidget] visitor send error', err) } catch {}
      alert(err.message || 'Failed to send')
    }
  }

  const onAttachClick = () => {
    if (!chatId) {
      alert('Please start the chat first, then attach a file.')
      return
    }
    fileInputRef.current?.click()
  }

  const uploadAndSendFile = async (file) => {
    if (!file || !chatId) return
    const maxSize = 15 * 1024 * 1024 // 15MB
    if (file.size > maxSize) { alert('File is too large (max 15MB).'); return }
    try {
      setUploadingFile(true)
      const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
      const path = `${chatId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9_.-]/g,'_')}`
      const { error: upErr } = await supabase.storage
        .from('public-chat-attachments')
        .upload(path, file, { cacheControl: '3600', upsert: false })
      if (upErr) throw upErr
      const { data: pub } = supabase.storage
        .from('public-chat-attachments')
        .getPublicUrl(path)
      const url = pub?.publicUrl
      if (!url) throw new Error('Failed to get public URL')
      await supabase.rpc('plc_add_message', { p_chat_id: chatId, p_sender: 'visitor', p_message: url })
      await loadMessages(chatId)
    } catch (err) {
      console.error('Upload failed:', err)
      if ((err?.message || '').toLowerCase().includes('bucket')) {
        alert('Storage bucket missing. Please create a bucket named "public-chat-attachments" and allow public read.')
      } else {
        alert(err.message || 'Upload failed')
      }
    } finally {
      setUploadingFile(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  useEffect(() => {
    if (!chatId) return
    loadMessages(chatId)
    const t = setInterval(() => loadMessages(chatId), 2000)
    const onVis = () => { if (document.visibilityState === 'visible') loadMessages(chatId) }
    try { document.addEventListener('visibilitychange', onVis) } catch {}
    return () => {
      clearInterval(t)
      try { document.removeEventListener('visibilitychange', onVis) } catch {}
    }
  }, [chatId])

  // Realtime subscribe for current chat to receive admin replies instantly
  useEffect(() => {
    if (!chatId) return
    // cleanup previous
    if (realtimeChannelRef.current) {
      try { supabase.removeChannel(realtimeChannelRef.current) } catch {}
      realtimeChannelRef.current = null
    }
    const ch = supabase
      .channel(`public-chat-${chatId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'public_chat_messages', filter: `chat_id=eq.${chatId}` }, (payload) => {
        const msg = payload?.new
        if (!msg || msg.chat_id !== chatId) return
        if (seenMessageIdsRef.current.has(msg.id)) return
        seenMessageIdsRef.current.add(msg.id)
        setMessages(prev => [...prev, msg])
      })
      .subscribe()
    realtimeChannelRef.current = ch
    return () => { try { supabase.removeChannel(ch) } catch {} }
  }, [chatId])

  // Keep dedupe set in sync with loaded messages
  useEffect(() => {
    const s = seenMessageIdsRef.current
    ;(messages || []).forEach(m => { if (m?.id) s.add(m.id) })
  }, [messages])

  // Track unread count when widget is closed
  useEffect(() => {
    if (!messages) return
    if (open) {
      const now = new Date().toISOString()
      setLastViewedAt(now)
      try { localStorage.setItem('publicChatLastViewedAt', now) } catch {}
      setUnread(0)
      return
    }
    if (!lastViewedAt) {
      setUnread(messages.length)
    } else {
      const count = messages.filter(m => m.created_at && new Date(m.created_at) > new Date(lastViewedAt)).length
      setUnread(count)
    }
  }, [messages, open])

  // Auto scroll to bottom on new messages
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, open])

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 60 }}
        className="group relative h-14 w-14 rounded-full shadow-xl bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:ring-primary-300 transition-all grid place-items-center"
        aria-label={open ? 'Close live chat' : 'Open live chat'}
      >
        <svg className="w-6 h-6 text-white transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] leading-none rounded-full h-5 min-w-[20px] px-1.5 grid place-items-center shadow">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{ position: 'fixed', right: 16, bottom: 80, zIndex: 60 }}
          className="w-80 sm:w-96 bg-white/95 backdrop-blur border border-gray-200 rounded-2xl shadow-2xl overflow-hidden animate-[fadeIn_.15s_ease-out]"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-white/20 grid place-items-center mr-2">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold leading-tight">Chat with us</p>
                  <p className="text-[11px] opacity-90 -mt-0.5">Typically replies in a few minutes</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {chatId && (
                  <button
                    onClick={async ()=>{
                      if (!window.confirm('Clear all messages and restart chat?')) return
                      try {
                        await supabase.rpc('plc_clear_messages', { p_chat_id: chatId })
                        setMessages([])
                        // Reset local state so user can re-enter info
                        setChatId(null)
                        try {
                          localStorage.removeItem('publicChatId')
                          localStorage.removeItem('publicChatGreeted')
                          localStorage.removeItem('publicChatLastViewedAt')
                        } catch {}
                      } catch (e) {
                        alert(e.message || 'Failed to clear chat')
                      }
                    }}
                    className="text-white/90 hover:text-white" title="Clear chat and restart" aria-label="Clear chat"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0a2 2 0 012-2h3.999A2 2 0 0116 7m-7 0H5"/></svg>
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-white/90 hover:text-white" aria-label="Close">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 max-h-[28rem] overflow-y-auto bg-gradient-to-b from-gray-50 to-white">
            {!chatId ? (
              <form onSubmit={startChat} className="space-y-3">
                <div className="space-y-3">
                  <div className="w-full flex justify-start">
                    <div className="max-w-[85%] inline-block px-3 py-2 rounded-2xl shadow-sm border bg-white border-blue-200 rounded-bl-sm">
                      <p className="text-sm">How can I help you, sir?</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Your email" className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Phone (optional)" className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <textarea value={initialMsg} onChange={e=>setInitialMsg(e.target.value)} rows={3} placeholder="How can we help?" className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <button disabled={loading} className={`w-full py-2 rounded-lg text-white shadow ${loading ? 'bg-primary-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}>{loading?'Starting…':'Start Chat'}</button>
                <p className="text-[11px] text-gray-500 text-center">We’ll only use your info to support this chat.</p>
              </form>
            ) : (
              <>
                <div className="space-y-2 mb-3">
                  {messages.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">No messages yet. Say hello! 👋</p>
                  ) : messages.map(m => {
                    const isImage = /^https?:\/\/.+\.(png|jpg|jpeg|gif|webp)$/i.test(m.message || '')
                    const isLink = /^https?:\/\//i.test(m.message || '')
                    return (
                      <div key={m.id} className={`w-full flex ${m.sender==='admin'?'justify-start':'justify-end'}`}>
                        <div className={`max-w-[85%] ${m.sender==='admin'?'items-start':'items-end'} flex flex-col`}>
                          <div className={`inline-block px-3 py-2 rounded-2xl shadow-sm border ${m.sender==='admin'?'bg-white border-blue-200':'bg-blue-600 text-white border-blue-600'} ${m.sender==='admin'?'rounded-bl-sm':'rounded-br-sm'}`}>
                            {isImage ? (
                              <a href={m.message} target="_blank" rel="noreferrer">
                                <img src={m.message} alt="attachment" className="max-h-48 rounded-lg" />
                              </a>
                            ) : isLink ? (
                              <a href={m.message} target="_blank" rel="noreferrer" className={`${m.sender==='admin'?'text-blue-600':'text-white underline'} break-all`}>
                                {m.message}
                              </a>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                            )}
                          </div>
                          <div className={`mt-1 text-[10px] ${m.sender==='admin'?'text-gray-500 text-left':'text-blue-600 text-right'}`}>
                            {m.sender==='admin'?'Admin':'You'} • {m.created_at? new Date(m.created_at).toLocaleTimeString():''}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  <div ref={messagesEndRef} />
                </div>
                <form onSubmit={sendMessage} className="flex items-center gap-2">
                  <div className="flex items-center flex-1 bg-white border border-gray-300 rounded-full px-3 py-1.5 focus-within:ring-2 focus-within:ring-primary-500">
                    <button type="button" onClick={onAttachClick} className="mr-2 text-gray-400 hover:text-gray-600" aria-label="Add attachment">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828L18 9.828a4 4 0 10-5.656-5.656L6.343 10.172"/></svg>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      onChange={(e)=> uploadAndSendFile(e.target.files?.[0])}
                    />
                    <input
                      value={outgoing}
                      onChange={e=>setOutgoing(e.target.value)}
                      onKeyDown={(e)=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); if(outgoing.trim()) sendMessage(e) } }}
                      className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400"
                      placeholder="Type a message…"
                    />
                  </div>
                  <button
                    title="Send"
                    className={`h-10 w-10 grid place-items-center rounded-full text-white shadow ${uploadingFile? 'bg-primary-300 cursor-wait' : outgoing.trim()? 'bg-primary-600 hover:bg-primary-700' : 'bg-primary-300 cursor-not-allowed'}`}
                    disabled={uploadingFile || !outgoing.trim()}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default PublicChatWidget


